<template>
  <USelectMenu
    v-bind="$props"
    v-model="modelSelectMenu"
    size="xl"
    :option-attribute="optionAttribute"
    :value-attribute="undefined"
    :searchable="searchableRef"
    searchable-placeholder="Entrez une requête pour voir les résultats."
    multiple
    :debounce
    :disabled
    :by="valueAttribute"
    @update:model-value="onUpdate"
  >
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot v-if="name !== 'label'" :name="name" v-bind="slotData" />
    </template>

    <template v-if="modelSelectMenu?.length" #label>
      <div class="flex flex-wrap gap-1 -ml-1">
        <UBadge
          v-for="option in modelSelectMenu"
          :key="option[valueAttribute]"
          class="pr-0 !pb-0 !pt-[0px] shadow inline-flex text-[0.8rem]"
          :color="isOptionHighlighted(option) ? highlightOptionColor : 'black'"
          size="xs"
          @mouseenter="emit('optionEnter', option)"
          @mouseleave="emit('optionLeave', option)"
        >
          <slot name="label-prefix" :option>
            {{ labelPrefix }}
          </slot>
          <span>{{ option[optionAttribute] }}</span>
          <ClientOnly>
            <UButton
              size="2xs"
              variant="link"
              :ui="{
                padding: {
                  '2xs': 'pr-1 pl-0.5',
                },
              }"
              color="white"
              class="hover:no-underline"
              @click.prevent="() => unSelect(option)"
            >
              <UIcon
                class="hover:!text-red-300 duration-300 -ml-0.5"
                name="mdi:close-circle"
              />
            </UButton>
          </ClientOnly>
        </UBadge>
      </div>
    </template>

    <template #option-empty="{ query }">
      <i18n-t keypath="components.forms.ATagList.noResults.query">
        <template #query>
          <q>{{ query }}</q>
        </template>
      </i18n-t>
    </template>

    <template #empty>
      <i>{{ $t("components.forms.ATagList.empty") }}</i>
    </template>
  </USelectMenu>
</template>

<script setup lang="ts">
import type { BadgeColor } from '#ui/types'
import dotObject from 'dot-object'

type ATagListSelectedOption = Record<string, any>

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    guardedAttributes?: string[]
    highlightOptionBy?: [string, any[]]
    highlightOptionColor?: BadgeColor
    keepBindingsAsObject?: boolean
    labelPrefix?: string
    optionAttribute?: string
    options?: any[]
    placeholder?: string
    searchable?:
      | ((search: string) => Promise<any[]>)
      | boolean
      | Extract<keyof Bff, `${string}/select`>
    searchableParams?: Record<string, any>
    valueAttribute?: string
  }>(),
  {
    disabled: false,
    guardedAttributes: () => [],
    highlightOptionBy: undefined,
    highlightOptionColor: 'red',
    keepBindingsAsObject: false,
    optionAttribute: 'name',
    searchable: false,
    searchableParams: () => ({}),
    valueAttribute: 'id',
  },
)

const emit = defineEmits<{
  change: []
  optionEnter: [ATagListSelectedOption]
  optionLeave: [ATagListSelectedOption]
  unselect: [ATagListSelectedOption]
}>()

const searchableRef = ref(props.searchable as any)

if (typeof searchableRef.value === 'string') {
  const searchableEndpoint = searchableRef.value
  searchableRef.value = async (search: string): Promise<any> => {
    return await $fetch(searchableEndpoint, {
      params: {
        search,
        ...props.searchableParams,
      },
    })
  }
}

const modelSelectMenu = ref<ATagListSelectedOption[]>([])
const oldSelectMenuValues = ref<ATagListSelectedOption[]>([])
const model = defineModel<ATagListSelectedOption[] | number[]>()
const debounce = process.env.TEST ? 10 : 400

function isOptionHighlighted(option: ATagListSelectedOption) {
  const highlightBy = props.highlightOptionBy

  if (!highlightBy) {
    return false
  }

  const [property, value] = highlightBy

  if (!(property in option)) {
    return false
  }

  return value.includes(option[property])
}

function onUpdate(newValues: ATagListSelectedOption[]) {
  const removedValue = oldSelectMenuValues.value.find(
    i =>
      !newValues.some(
        j => j[props.valueAttribute] === i[props.valueAttribute],
      ),
  )
  updateModelValue()
  emit('change')
  if (removedValue) {
    emit('unselect', removedValue)
  }
}

function unSelect(option: ATagListSelectedOption) {
  modelSelectMenu.value = modelSelectMenu.value?.filter(
    i => i[props.valueAttribute] !== option[props.valueAttribute],
  )
  updateModelValue()
  emit('unselect', option)
}

function updateModelValue() {
  if (props.keepBindingsAsObject) {
    model.value = modelSelectMenu.value
    return
  }

  model.value = modelSelectMenu.value?.map(i => i[props.valueAttribute])
}

watch(model, () => {
  if (props.keepBindingsAsObject) {
    modelSelectMenu.value = model.value as typeof modelSelectMenu.value
    if (import.meta.client) {
      window.dispatchEvent(new Event('resize'))
    }
    return
  }

  modelSelectMenu.value = modelSelectMenu.value?.filter(item =>
    model.value?.includes(item[props.valueAttribute]),
  )
})

watch(modelSelectMenu, (_, oldValue) => {
  oldSelectMenuValues.value = oldValue ?? []
})

watchEffect(() => {
  if (!modelSelectMenu.value) {
    return
  }

  modelSelectMenu.value = modelSelectMenu.value.map((item) => {
    const filtered = {} as any
    dotObject.copy(
      props.optionAttribute.toString(),
      props.optionAttribute.toString(),
      item,
      filtered,
    )
    dotObject.copy(
      props.valueAttribute.toString(),
      props.valueAttribute.toString(),
      item,
      filtered,
    )

    for (const attribute of props.guardedAttributes) {
      dotObject.copy(attribute, attribute, item, filtered)
    }

    return filtered
  })
})

modelSelectMenu.value = model.value as ATagListSelectedOption[]
onMounted(updateModelValue)
</script>
